import dayjs from 'dayjs';

import { GroupLeadsBy, DashboardState } from './dashboardv2.store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DemoRevenueTrigger {
    start: number;
    end: number;
    currency: string;
    zone: string;
}

export interface DemoPipelineTrigger {
    start: number;
    end: number;
    currency: string;
    groupLeadsBy: GroupLeadsBy;
    source?: string | null;
    jobOrigin?: string | null;
    estimateMethod?: string | null;
    estimator?: string | null;
    salesAgent?: string | null;
    utmSource?: string | null;
    utmMedium?: string | null;
    utmCampaign?: string | null;
    utmContent?: string | null;
    utmTerm?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function seedRandom(seed: number): number {
    const x = Math.sin(seed + 1) * 10000;
    return x - Math.floor(x);
}

function periodDays(start: number, end: number): number {
    return Math.max(1, Math.round((end - start) / 86400));
}

function currencyRate(currency: string): number {
    return currency === 'CAD' ? 1.36 : 1;
}

function displayCurrency(currency: string): string {
    return currency === 'All' ? 'USD' : currency;
}

function scaleAmount(base: number, days: number, currency: string): number {
    return Math.round(base * (days / 30) * currencyRate(currency));
}

/**
 * Scales a base count to the given number of days.
 * @param minimum  Floor applied after rounding. Use `1` for top-level KPI tiles
 *                 that must always show at least one unit; leave at the default `0`
 *                 for per-group / per-row buckets so short ranges can fall to zero.
 */
function scaleCount(base: number, days: number, minimum = 0): number {
    return Math.max(minimum, Math.round(base * (days / 30)));
}

function buildDailySeries(
    start: number,
    end: number,
    dailyBase: number,
    opts: { weekendFactor?: number; variance?: number; trend?: number; timezone?: string } = {},
): string[][] {
    const { weekendFactor = 0.3, variance = 0.25, trend = 0.002, timezone } = opts;
    const rows: string[][] = [];
    // Use the branding timezone when available so YYYY-MM-DD labels align with
    // the selected period even when the browser timezone differs.
    const toDay = (unix: number) => timezone
        ? dayjs(unix * 1000).tz(timezone).startOf('day')
        : dayjs.unix(unix).startOf('day');
    let current = toDay(start);
    const endDay = toDay(end);
    let dayIndex = 0;
    while (!current.isAfter(endDay)) {
        const dow = current.day();
        const isWeekend = dow === 0 || dow === 6;
        const trendFactor = 1 + trend * dayIndex;
        const jitter = 1 + (seedRandom(dayIndex) - 0.5) * 2 * variance;
        let value = dailyBase * trendFactor * jitter;
        if (isWeekend) { value *= weekendFactor; }
        rows.push([ current.format('YYYY-MM-DD'), value.toFixed(2) ]);
        current = current.add(1, 'day');
        dayIndex++;
    }
    return rows;
}

// ---------------------------------------------------------------------------
// Funnel definition
//
// The pipeline represents each job's CURRENT stage.  Every job appears in
// exactly one row.  Fractions below describe what portion of all leads
// currently sit at each stage/status combination.  They must sum to 1.
// ---------------------------------------------------------------------------

interface FunnelSlice {
    stage: 'Lead' | 'Estimate' | 'Booking' | 'Invoice';
    closureReason: string; // 'None' = open
    fraction: number;
}

const FUNNEL: FunnelSlice[] = [
    { stage: 'Lead',     closureReason: 'None',         fraction: 0.06 },
    { stage: 'Lead',     closureReason: 'not-a-fit',    fraction: 0.05 },
    { stage: 'Lead',     closureReason: 'no-response',  fraction: 0.04 },
    { stage: 'Estimate', closureReason: 'None',         fraction: 0.12 },
    { stage: 'Estimate', closureReason: 'price',        fraction: 0.04 },
    { stage: 'Estimate', closureReason: 'no-response',  fraction: 0.03 },
    { stage: 'Booking',  closureReason: 'None',         fraction: 0.22 },
    { stage: 'Booking',  closureReason: 'cancelled',    fraction: 0.04 },
    { stage: 'Invoice',  closureReason: 'complete',     fraction: 0.30 },
    { stage: 'Invoice',  closureReason: 'not-complete', fraction: 0.10 },
];
// Sum: 0.06+0.05+0.04 + 0.12+0.04+0.03 + 0.22+0.04 + 0.30+0.10 = 1.00
// Conversion story: 85% reach Estimate, 66% reach Booking, 40% complete

/**
 * Distribute `total` integer across funnel slices proportionally,
 * adjusting the largest slice to absorb any rounding remainder.
 */
function distributeFunnel(total: number): number[] {
    const counts = FUNNEL.map(({ fraction }) => Math.floor(total * fraction));
    let remainder = total - counts.reduce((a, b) => a + b, 0);
    // Add remainder to the slice with the largest fractional part
    const fractionals = FUNNEL.map(({ fraction }, i) => ({
        i,
        frac: (total * fraction) - counts[i],
    })).sort((a, b) => b.frac - a.frac);
    for (let r = 0; r < remainder; r++) {
        counts[fractionals[r % fractionals.length].i]++;
        remainder--;
        r--;  // re-check after increment
        if (remainder === 0) { break; }
    }
    return counts;
}

// ---------------------------------------------------------------------------
// Group definitions per groupLeadsBy dimension
//
// Each entry: [label, proportion, baseLeadCount30d, baseRevenue30d]
// Proportions within each set must sum to 1.
// ---------------------------------------------------------------------------

interface GroupConfig {
    label: string;
    proportion: number;
    /** Total jobs from this group in a 30-day period (canonical count) */
    baseCount: number;
    /** Total revenue from this group in a 30-day period */
    baseRevenue: number;
}

const GROUP_SETS: Record<GroupLeadsBy, GroupConfig[]> = {
    'Source': [
        { label: 'Google',        proportion: 0.30, baseCount: 38, baseRevenue: 320400 },
        { label: 'Referral',      proportion: 0.24, baseCount: 31, baseRevenue: 268900 },
        { label: 'Yelp',          proportion: 0.17, baseCount: 22, baseRevenue: 178500 },
        { label: 'Facebook',      proportion: 0.14, baseCount: 18, baseRevenue: 143200 },
        { label: 'Website',       proportion: 0.09, baseCount: 12, baseRevenue:  97600 },
        { label: 'Not Specified', proportion: 0.06, baseCount:  6, baseRevenue:  38600 },
    ],
    'Job Origin': [
        { label: 'Online Form',       proportion: 0.32, baseCount: 41, baseRevenue: 342100 },
        { label: 'Phone Call',        proportion: 0.28, baseCount: 36, baseRevenue: 299400 },
        { label: 'Repeat Customer',   proportion: 0.18, baseCount: 23, baseRevenue: 187700 },
        { label: 'Partner Referral',  proportion: 0.14, baseCount: 18, baseRevenue: 143200 },
        { label: 'Walk-in',           proportion: 0.08, baseCount:  9, baseRevenue:  74800 },
    ],
    'Estimate Method': [
        { label: 'Virtual',     proportion: 0.48, baseCount: 61, baseRevenue: 492500 },
        { label: 'On-site',     proportion: 0.38, baseCount: 48, baseRevenue: 392800 },
        { label: 'Self-Survey', proportion: 0.14, baseCount: 18, baseRevenue: 161900 },
    ],
    'Estimator': [
        { label: 'Alex.Johnson.aaa-111',    proportion: 0.35, baseCount: 45, baseRevenue: 374200 },
        { label: 'Jordan.Smith.bbb-222',    proportion: 0.30, baseCount: 38, baseRevenue: 317400 },
        { label: 'Taylor.Williams.ccc-333', proportion: 0.25, baseCount: 32, baseRevenue: 264600 },
        { label: 'Morgan.Brown.ddd-444',    proportion: 0.10, baseCount: 12, baseRevenue:  91000 },
    ],
    'Sales Agent': [
        { label: 'Alex.Johnson.aaa-111',    proportion: 0.40, baseCount: 51, baseRevenue: 424800 },
        { label: 'Jordan.Smith.bbb-222',    proportion: 0.35, baseCount: 44, baseRevenue: 371800 },
        { label: 'Taylor.Williams.ccc-333', proportion: 0.25, baseCount: 32, baseRevenue: 250600 },
    ],
    'UTM Source': [
        { label: 'google',   proportion: 0.45, baseCount: 57, baseRevenue: 476700 },
        { label: 'facebook', proportion: 0.22, baseCount: 28, baseRevenue: 232900 },
        { label: 'yelp',     proportion: 0.18, baseCount: 23, baseRevenue: 191200 },
        { label: 'direct',   proportion: 0.15, baseCount: 19, baseRevenue: 146400 },
    ],
    'UTM Medium': [
        { label: 'cpc',      proportion: 0.42, baseCount: 53, baseRevenue: 443600 },
        { label: 'organic',  proportion: 0.28, baseCount: 36, baseRevenue: 297800 },
        { label: 'referral', proportion: 0.20, baseCount: 25, baseRevenue: 208100 },
        { label: 'email',    proportion: 0.10, baseCount: 13, baseRevenue: 97700 },
    ],
    'UTM Campaign': [
        { label: 'spring-promo',    proportion: 0.38, baseCount: 48, baseRevenue: 403200 },
        { label: 'brand-awareness', proportion: 0.30, baseCount: 38, baseRevenue: 318400 },
        { label: 'retargeting',     proportion: 0.20, baseCount: 25, baseRevenue: 212100 },
        { label: 'local-search',    proportion: 0.12, baseCount: 16, baseRevenue: 113500 },
    ],
    'UTM Content': [
        { label: 'banner-a', proportion: 0.35, baseCount: 44, baseRevenue: 372600 },
        { label: 'banner-b', proportion: 0.30, baseCount: 38, baseRevenue: 317400 },
        { label: 'text-ad',  proportion: 0.25, baseCount: 32, baseRevenue: 264600 },
        { label: 'video',    proportion: 0.10, baseCount: 13, baseRevenue:  92600 },
    ],
    'UTM Term': [
        { label: 'moving company',      proportion: 0.38, baseCount: 48, baseRevenue: 403200 },
        { label: 'local movers',        proportion: 0.28, baseCount: 36, baseRevenue: 299400 },
        { label: 'residential moving',  proportion: 0.20, baseCount: 25, baseRevenue: 212100 },
        { label: 'moving services',     proportion: 0.14, baseCount: 18, baseRevenue: 132500 },
    ],
};

function getActiveFilter(groupLeadsBy: GroupLeadsBy, t: DemoPipelineTrigger): string | null {
    switch (groupLeadsBy) {
        case 'Source':          return t.source ?? null;
        case 'Job Origin':      return t.jobOrigin ?? null;
        case 'Estimate Method': return t.estimateMethod ?? null;
        case 'Estimator':       return t.estimator ?? null;
        case 'Sales Agent':     return t.salesAgent ?? null;
        case 'UTM Source':      return t.utmSource ?? null;
        case 'UTM Medium':      return t.utmMedium ?? null;
        case 'UTM Campaign':    return t.utmCampaign ?? null;
        case 'UTM Content':     return t.utmContent ?? null;
        case 'UTM Term':        return t.utmTerm ?? null;
        default:                return null;
    }
}

// ---------------------------------------------------------------------------
// 30-day USD baseline totals
// ---------------------------------------------------------------------------

const BASE = {
    realizedRevenue:         284750,
    scheduledRevenue:        517300,
    expectedRevenue:         847200,
    distanceBooked:           18420,
    distanceCompleted:        12150,
    travelTimeBooked:          3240,
    travelTimeCompleted:       2180,

    pastRealizedRevenue:     254200,
    pastScheduledRevenue:    468100,
    pastExpectedRevenue:     753800,
    pastDistanceBooked:       16800,
    pastDistanceCompleted:    11200,
    pastTravelTimeBooked:      2980,
    pastTravelTimeCompleted:   1940,

    invoiceCount:      42,  invoiceTotal:     284750,
    pastInvoiceCount:  38,  pastInvoiceTotal: 254200,
    jobCount:         127,  jobTotal:         847200,
    pastJobCount:     118,  pastJobTotal:     753800,
    bookingCount:      71,  bookingTotal:     517300,
    pastBookingCount:  65,  pastBookingTotal: 468100,
    scheduledCount:    58,  scheduledTotal:   423800,
    pastScheduledCount:52,  pastScheduledTotal:378200,

    dailyTransactions:    9500,
    dailyEventsCreated:      4,
    dailyEventsHappening:    3,
};

// ---------------------------------------------------------------------------
// Revenue tab patch
// ---------------------------------------------------------------------------

export function getDemoRevenuePatch(trigger: DemoRevenueTrigger): Partial<DashboardState> {
    const days = periodDays(trigger.start, trigger.end);
    const cur = trigger.currency;
    const dc = displayCurrency(cur);
    const tz = trigger.zone;

    const amt = (base: number) => scaleAmount(base, days, cur);
    // Top-level KPI tile counts must show at least 1 so the UI never renders zero.
    const cnt = (base: number) => scaleCount(base, days, 1);

    return {
        realizedRevenue:  { currency: dc, amount: amt(BASE.realizedRevenue) },
        scheduledRevenue: { currency: dc, amount: amt(BASE.scheduledRevenue) },
        expectedRevenue:  { currency: dc, amount: amt(BASE.expectedRevenue) },
        sharedRevenue:    { currency: dc, amount: 0 },
        distanceBooked:    Math.round(BASE.distanceBooked * (days / 30)),
        distanceCompleted: Math.round(BASE.distanceCompleted * (days / 30)),
        travelTimeBooked:    Math.round(BASE.travelTimeBooked * (days / 30)),
        travelTimeCompleted: Math.round(BASE.travelTimeCompleted * (days / 30)),
        revenueAndLogisticsLoading: false,
        revenueAndLogisticsError: null,

        pastRealizedRevenue:  { currency: dc, amount: amt(BASE.pastRealizedRevenue) },
        pastScheduledRevenue: { currency: dc, amount: amt(BASE.pastScheduledRevenue) },
        pastExpectedRevenue:  { currency: dc, amount: amt(BASE.pastExpectedRevenue) },
        pastSharedRevenue:    { currency: dc, amount: 0 },
        pastDistanceBooked:    Math.round(BASE.pastDistanceBooked * (days / 30)),
        pastDistanceCompleted: Math.round(BASE.pastDistanceCompleted * (days / 30)),
        pastTravelTimeBooked:    Math.round(BASE.pastTravelTimeBooked * (days / 30)),
        pastTravelTimeCompleted: Math.round(BASE.pastTravelTimeCompleted * (days / 30)),
        pastRevenueAndLogisticsLoading: false,

        invoiceSizeCount:    cnt(BASE.invoiceCount),  invoiceSizeTotal:    amt(BASE.invoiceTotal),
        pastInvoiceSizeCount:cnt(BASE.pastInvoiceCount), pastInvoiceSizeTotal:amt(BASE.pastInvoiceTotal),
        averageInvoiceSizeLoading: false, averageInvoiceSizeError: null,
        pastAverageInvoiceSizeLoading: false,

        jobSizeCount:    cnt(BASE.jobCount),  jobSizeTotal:    amt(BASE.jobTotal),
        pastJobSizeCount:cnt(BASE.pastJobCount), pastJobSizeTotal:amt(BASE.pastJobTotal),
        averageJobSizeLoading: false, averageJobSizeError: null,
        pastAverageJobSizeLoading: false,

        bookingSizeCount:    cnt(BASE.bookingCount),  bookingSizeTotal:    amt(BASE.bookingTotal),
        pastBookingSizeCount:cnt(BASE.pastBookingCount), pastBookingSizeTotal:amt(BASE.pastBookingTotal),
        averageBookingSizeLoading: false, averageBookingSizeError: null,
        pastAverageBookingSizeLoading: false,

        scheduledJobSizeCount:    cnt(BASE.scheduledCount),
        scheduledJobSizeTotal:    amt(BASE.scheduledTotal),
        pastScheduledJobSizeCount:cnt(BASE.pastScheduledCount),
        pastScheduledJobSizeTotal:amt(BASE.pastScheduledTotal),
        averageScheduledJobSizeLoading: false, averageScheduledJobSizeError: null,
        pastAverageScheduledJobSizeLoading: false, pastAverageScheduledJobSizeError: null,

        transactionsPaidPerDay: buildDailySeries(
            trigger.start, trigger.end,
            BASE.dailyTransactions * currencyRate(cur),
            { weekendFactor: 0.2, variance: 0.3, timezone: tz },
        ),
        eventsCreatedPerDay: buildDailySeries(
            trigger.start, trigger.end,
            BASE.dailyEventsCreated,
            { weekendFactor: 0.4, variance: 0.2, timezone: tz },
        ),
        eventsHappeningPerDay: buildDailySeries(
            trigger.start, trigger.end,
            BASE.dailyEventsHappening,
            { weekendFactor: 0.5, variance: 0.25, timezone: tz },
        ),
        activityLoading: false,
        activityError: null,

        eventsCreated:   buildEventRows(days, cur, dc, 'created'),
        eventsHappening: buildEventRows(days, cur, dc, 'happening'),
        eventsCreatedLoading: false,  eventsCreatedError: null,
        eventsHappeningLoading: false, eventsHappeningError: null,
    };
}

// ---------------------------------------------------------------------------
// Engine job rows (the individual job cards shown in each pipeline section)
//
// Row format matches selectedJobsColumns:
//   [job, customer, jobStage, jobStatus, estimator, discountedSubTotal, jobCreatedAt]
//
// job      = "MR-XXXXX <move /jobs/{uuid}?>"
// customer = "Full Name <customer /customers/{uuid}?>"
// estimator = "Full Name <user /users/{uuid}?>" or 'None'
//
// engineJobs is keyed by encodeKeyForGraphQL(jobInfoCountKey).
// encodeKeyForGraphQL replaces - and spaces with ___.
// ---------------------------------------------------------------------------

function jobRow(
    code: string,
    uuid: string,
    customer: string,
    custId: string,
    stage: string,
    status: string,
    estimator: string,
    estId: string,
    amount: string,
    date: string,
): string[] {
    const est = estimator === 'None'
        ? 'None'
        : `${estimator} <user /users/${estId}?>`;
    return [
        `${code} <move /jobs/${uuid}?>`,
        `${customer} <customer /customers/${custId}?>`,
        stage,
        status,
        est,
        amount,
        date,
    ];
}

function demoEngineJobs(): DashboardState['engineJobs'] {
    // Returns a 'YYYY-MM-DD' string relative to today.
    // Positive offsetDays go backwards in time; negative values go forwards.
    const d = (offsetDays: number) =>
        dayjs().subtract(offsetDays, 'day').format('YYYY-MM-DD');

    const EST_A = { name: 'Alex Johnson',   id: 'est-aaa-111' };
    const EST_B = { name: 'Jordan Smith',   id: 'est-bbb-222' };
    const EST_C = { name: 'Taylor Williams', id: 'est-ccc-333' };

    /* eslint-disable max-len */
    const openLeads = [
        jobRow('MR-00841', 'job-0841-uuid', 'Sarah Mitchell',  'cust-0001', 'Lead', 'Open', EST_A.name, EST_A.id, '7200.00',  d(2)),
        jobRow('MR-00839', 'job-0839-uuid', 'Kevin Patel',     'cust-0002', 'Lead', 'Open', EST_B.name, EST_B.id, '5800.00',  d(3)),
        jobRow('MR-00835', 'job-0835-uuid', 'Diana Nguyen',    'cust-0003', 'Lead', 'Open', 'None',     '',       '9400.00',  d(5)),
        jobRow('MR-00831', 'job-0831-uuid', 'Marcus Torres',   'cust-0004', 'Lead', 'Open', EST_C.name, EST_C.id, '6100.00',  d(7)),
        jobRow('MR-00828', 'job-0828-uuid', 'Linda Yamamoto',  'cust-0005', 'Lead', 'Open', EST_A.name, EST_A.id, '11500.00', d(9)),
    ];

    const closedLeads = [
        jobRow('MR-00790', 'job-0790-uuid', 'Brian Okafor',    'cust-0011', 'Lead', 'Closed', EST_B.name, EST_B.id, '4200.00', d(18)),
        jobRow('MR-00782', 'job-0782-uuid', 'Grace Liu',       'cust-0012', 'Lead', 'Closed', EST_A.name, EST_A.id, '7800.00', d(21)),
        jobRow('MR-00775', 'job-0775-uuid', 'Carlos Mendez',   'cust-0013', 'Lead', 'Closed', 'None',     '',       '5100.00', d(23)),
        jobRow('MR-00769', 'job-0769-uuid', 'Priya Shah',      'cust-0014', 'Lead', 'Closed', EST_C.name, EST_C.id, '6600.00', d(25)),
        jobRow('MR-00761', 'job-0761-uuid', 'Tom Kowalski',    'cust-0015', 'Lead', 'Closed', EST_A.name, EST_A.id, '3900.00', d(28)),
    ];

    const openEstimates = [
        jobRow('MR-00820', 'job-0820-uuid', 'Rachel Kim',      'cust-0021', 'Estimate', 'Open', EST_B.name, EST_B.id, '8900.00',  d(10)),
        jobRow('MR-00815', 'job-0815-uuid', 'David Foster',    'cust-0022', 'Estimate', 'Open', EST_A.name, EST_A.id, '12400.00', d(12)),
        jobRow('MR-00808', 'job-0808-uuid', 'Natalie Cruz',    'cust-0023', 'Estimate', 'Open', EST_C.name, EST_C.id, '7300.00',  d(13)),
        jobRow('MR-00803', 'job-0803-uuid', 'James Park',      'cust-0024', 'Estimate', 'Open', EST_B.name, EST_B.id, '6800.00',  d(15)),
        jobRow('MR-00797', 'job-0797-uuid', 'Monica Wells',    'cust-0025', 'Estimate', 'Open', EST_A.name, EST_A.id, '10200.00', d(16)),
    ];

    const closedEstimates = [
        jobRow('MR-00750', 'job-0750-uuid', 'Anthony Rivera',  'cust-0031', 'Estimate', 'Closed', EST_C.name, EST_C.id, '5500.00', d(30)),
        jobRow('MR-00742', 'job-0742-uuid', 'Sophie Anderson', 'cust-0032', 'Estimate', 'Closed', EST_A.name, EST_A.id, '8100.00', d(33)),
        jobRow('MR-00736', 'job-0736-uuid', 'Ryan Petrowski',  'cust-0033', 'Estimate', 'Closed', EST_B.name, EST_B.id, '4700.00', d(35)),
        jobRow('MR-00729', 'job-0729-uuid', 'Elena Petrov',    'cust-0034', 'Estimate', 'Closed', EST_C.name, EST_C.id, '6200.00', d(37)),
        jobRow('MR-00721', 'job-0721-uuid', 'Omar Hassan',     'cust-0035', 'Estimate', 'Closed', EST_A.name, EST_A.id, '5900.00', d(39)),
    ];

    const openBookings = [
        jobRow('MR-00780', 'job-0780-uuid', 'Jessica Hoffman', 'cust-0041', 'Booking', 'Open', EST_B.name, EST_B.id, '9800.00',  d(14)),
        jobRow('MR-00773', 'job-0773-uuid', 'Daniel Reyes',    'cust-0042', 'Booking', 'Open', EST_A.name, EST_A.id, '13200.00', d(17)),
        jobRow('MR-00765', 'job-0765-uuid', 'Amanda Clarke',   'cust-0043', 'Booking', 'Open', EST_C.name, EST_C.id, '7600.00',  d(19)),
        jobRow('MR-00758', 'job-0758-uuid', 'Steven Nguyen',   'cust-0044', 'Booking', 'Open', EST_B.name, EST_B.id, '11100.00', d(20)),
        jobRow('MR-00751', 'job-0751-uuid', 'Hannah Brooks',   'cust-0045', 'Booking', 'Open', EST_A.name, EST_A.id, '8400.00',  d(22)),
    ];

    const closedBookings = [
        jobRow('MR-00700', 'job-0700-uuid', 'Nathan Fowler',   'cust-0051', 'Booking', 'Closed', EST_C.name, EST_C.id, '5700.00', d(40)),
        jobRow('MR-00691', 'job-0691-uuid', 'Laura Santos',    'cust-0052', 'Booking', 'Closed', EST_A.name, EST_A.id, '7400.00', d(42)),
        jobRow('MR-00683', 'job-0683-uuid', 'Patrick Murphy',  'cust-0053', 'Booking', 'Closed', EST_B.name, EST_B.id, '4900.00', d(45)),
        jobRow('MR-00675', 'job-0675-uuid', 'Chloe Evans',     'cust-0054', 'Booking', 'Closed', EST_C.name, EST_C.id, '6300.00', d(47)),
        jobRow('MR-00668', 'job-0668-uuid', 'Victor Nkosi',    'cust-0055', 'Booking', 'Closed', EST_A.name, EST_A.id, '5200.00', d(50)),
    ];

    const completedInvoices = [
        jobRow('MR-00710', 'job-0710-uuid', 'Stephanie Cole',  'cust-0061', 'Invoice', 'Closed', EST_B.name, EST_B.id, '10400.00', d(8)),
        jobRow('MR-00705', 'job-0705-uuid', 'Andrew Tanaka',   'cust-0062', 'Invoice', 'Closed', EST_C.name, EST_C.id, '8700.00',  d(11)),
        jobRow('MR-00698', 'job-0698-uuid', 'Michelle Turner', 'cust-0063', 'Invoice', 'Closed', EST_A.name, EST_A.id, '14100.00', d(13)),
        jobRow('MR-00693', 'job-0693-uuid', 'George Obi',      'cust-0064', 'Invoice', 'Closed', EST_B.name, EST_B.id, '7900.00',  d(15)),
        jobRow('MR-00689', 'job-0689-uuid', 'Isabella Flynn',  'cust-0065', 'Invoice', 'Closed', EST_C.name, EST_C.id, '9300.00',  d(17)),
    ];

    const openInvoices = [
        jobRow('MR-00745', 'job-0745-uuid', 'Robert Castillo', 'cust-0071', 'Invoice', 'Open', EST_A.name, EST_A.id, '11800.00', d(6)),
        jobRow('MR-00738', 'job-0738-uuid', 'Christina Yuen',  'cust-0072', 'Invoice', 'Open', EST_B.name, EST_B.id, '9100.00',  d(9)),
        jobRow('MR-00732', 'job-0732-uuid', 'Derek Barnes',    'cust-0073', 'Invoice', 'Open', EST_C.name, EST_C.id, '6700.00',  d(12)),
        jobRow('MR-00726', 'job-0726-uuid', 'Fatima Al-Rashid','cust-0074', 'Invoice', 'Open', EST_A.name, EST_A.id, '8300.00',  d(14)),
        jobRow('MR-00719', 'job-0719-uuid', 'Tyler Holbrook',  'cust-0075', 'Invoice', 'Open', EST_B.name, EST_B.id, '12600.00', d(16)),
    ];
    /* eslint-enable max-len */

    const wrapRows = (rows: string[][]) => ({ data: { rows } });

    return {
        // Top-level Lead stage sections
        'Lead__open':    wrapRows(openLeads),
        'Lead__closed':  wrapRows(closedLeads),

        // Estimate stage sections
        'Estimate':      wrapRows(openEstimates),  // "Converted to Estimate" section
        'Estimate__open':   wrapRows(openEstimates),
        'Estimate__closed': wrapRows(closedEstimates),

        // Booking stage sections
        'Booking':       wrapRows(openBookings),   // "Converted to Booking" section
        'Booking__open':    wrapRows(openBookings),
        'Booking__closed':  wrapRows(closedBookings),

        // Invoice stage sections
        'Invoice':                           wrapRows(completedInvoices),
        'Invoice__closed__complete':         wrapRows(completedInvoices),
        'Invoice__closed__not___complete':   wrapRows(openInvoices),
    };
}

// ---------------------------------------------------------------------------
// Pipeline patch — internally consistent funnel
//
// Key invariant: for every group, sum of all pipeline rows for that group
// equals the Sources widget count for that group.  Each job appears in
// exactly one row (its current stage/status).
//
// The Sankey flows then automatically satisfy:
//   groupByCount[g] = sources count for g
//   Lead node inflow  = sum(groupByCount) = total jobs
//   Lead node outflow = leadOpen + leadClosed + progressedToEstimate = same total
//   … and so on for each subsequent stage.
// ---------------------------------------------------------------------------

export function getDemoPipelinePatch(trigger: DemoPipelineTrigger): Partial<DashboardState> {
    const days = periodDays(trigger.start, trigger.end);
    const cur = trigger.currency;
    const groupLeadsBy = trigger.groupLeadsBy ?? 'Source';
    const activeFilter = getActiveFilter(groupLeadsBy, trigger);

    const groups = (GROUP_SETS[groupLeadsBy] ?? GROUP_SETS['Source'])
        .filter(g => !activeFilter || g.label === activeFilter);

    const rows: string[][] = [];

    for (const group of groups) {
        // Scale total count and revenue for this group to the selected period.
        // totalCount and totalRevenue are the canonical values that the Sources
        // widget also uses, so they must match exactly.
        const totalCount = scaleCount(group.baseCount, days);
        const totalRevenue = scaleAmount(group.baseRevenue, days, cur);

        // Distribute totalCount across funnel slices — sums exactly to totalCount.
        const counts = distributeFunnel(totalCount);

        // Distribute totalRevenue proportionally, absorbing rounding into the
        // last non-zero row so that sum(rowRevenues) === totalRevenue exactly.
        let revenueRemaining = totalRevenue;
        const revenues: number[] = [];
        const nonZeroIndices = counts.map((c, i) => c > 0 ? i : -1).filter(i => i >= 0);
        for (let i = 0; i < FUNNEL.length; i++) {
            if (counts[i] <= 0) {
                revenues.push(0);
                continue;
            }
            const isLast = i === nonZeroIndices[nonZeroIndices.length - 1];
            const rev = isLast
                ? revenueRemaining
                : Math.round(totalRevenue * (counts[i] / totalCount));
            revenues.push(rev);
            revenueRemaining -= rev;
        }

        for (let i = 0; i < FUNNEL.length; i++) {
            const { stage, closureReason } = FUNNEL[i];
            if (counts[i] <= 0) { continue; }

            // Open jobs use attentionExplanation = 'None' (truthy), matching what the
            // real backend emits.  An empty string would cause pipelineBreakdown$ to
            // create both a stage__status aggregate key AND a stage__status__ (empty
            // reason) key; calculateLabelCount would then include both, doubling the
            // flow values for every open-stage node in the Sankey chart.
            const attn = closureReason === 'None' ? 'None' : '';

            rows.push([
                `${stage};${group.label};${closureReason};${attn}`,
                String(counts[i]),
                revenues[i].toFixed(2),
            ]);
        }
    }

    return {
        pipeline: rows,
        pipelineLoading: false,
        pipelineError: null,
        engineJobs: demoEngineJobs(),
        engineJobsLoading: false,
        engineJobsError: null,
    };
}


// ---------------------------------------------------------------------------
// Event rows
// ---------------------------------------------------------------------------

const EVENT_HEADERS = [
    'Event Type',
    'Net Subtotal',
    'Event Count',
    'Average Net Subtotal per Event',
    'Currency',
];

const EVENT_BASE_CREATED: Array<[string, number, number]> = [
    [ 'moving',   248400, 36 ],
    [ 'packing',   19250,  7 ],
    [ 'unpacking', 11800,  5 ],
    [ 'delivery',   5300,  3 ],
];

const EVENT_BASE_HAPPENING: Array<[string, number, number]> = [
    [ 'moving',   198700, 29 ],
    [ 'packing',   14300,  5 ],
    [ 'unpacking',  8400,  4 ],
    [ 'delivery',   3200,  2 ],
];

function buildEventRows(
    days: number,
    currency: string,
    dc: string,
    kind: 'created' | 'happening',
): { headers: string[]; rows: string[][] } {
    const base = kind === 'created' ? EVENT_BASE_CREATED : EVENT_BASE_HAPPENING;
    const rows = base.map(([ type, baseTotal, baseCount ]) => {
        const total = scaleAmount(baseTotal, days, currency);
        const count = scaleCount(baseCount, days);
        const avg = count > 0 ? (total / count).toFixed(2) : '0.00';
        return [ type, total.toFixed(2), String(count), avg, dc ];
    });
    return { headers: EVENT_HEADERS, rows };
}
